import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { formatSAR } from "@/lib/i18n";

export default function CartDrawer({ open, onOpenChange }) {
  const { cart, removeFromCart, updateQty, cartSubtotal, cartVat, cartTotal, lineUnitPrice, t, lang } = useApp();
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={lang === "ar" ? "left" : "right"} className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-6 py-5 border-b border-border">
          <SheetTitle data-testid="cart-drawer-title" className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            {t.cart}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-auto px-6 py-4">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
              <ShoppingBag className="h-12 w-12 mb-3 opacity-40" />
              <p data-testid="cart-empty-message">{t.cartEmpty}</p>
            </div>
          ) : (
            <ul className="space-y-4">
              {cart.map((item) => {
                const p = item.product;
                const name = lang === "ar" ? p.name_ar : p.name_en;
                const variant = (p.variants || []).find((v) => v.id === item.variant_id);
                const service = (p.service_options || []).find((s) => s.id === item.service_id);
                const variantName = variant ? (lang === "ar" ? variant.name_ar : variant.name_en) : null;
                const serviceName = service ? (lang === "ar" ? service.name_ar : service.name_en) : null;
                const unitPrice = lineUnitPrice(item);
                const img = variant?.preview_image || p.preview_image;
                return (
                  <li
                    key={`${p.id}-${item.variant_id}-${item.service_id}`}
                    data-testid={`cart-item-${p.id}`}
                    className="flex gap-3 pb-4 border-b border-border last:border-b-0"
                  >
                    <img src={img} alt={name} className="h-20 w-20 rounded-md object-cover bg-muted flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm line-clamp-1">{name}</p>
                      <div className="text-[11px] text-muted-foreground mt-0.5 space-y-0.5">
                        {variantName && <p>{t.style}: {variantName}</p>}
                        {serviceName && <p>{t.serviceType}: {serviceName}</p>}
                      </div>
                      <p className="text-sm font-semibold mt-1">{formatSAR(unitPrice, lang)}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateQty(p.id, item.variant_id, item.service_id, item.qty - 1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="text-sm w-6 text-center">{item.qty}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateQty(p.id, item.variant_id, item.service_id, item.qty + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 ms-auto text-muted-foreground hover:text-destructive"
                          onClick={() => removeFromCart(p.id, item.variant_id, item.service_id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-border px-6 py-5 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t.subtotal}</span>
              <span data-testid="cart-subtotal">{formatSAR(cartSubtotal, lang)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t.vat}</span>
              <span data-testid="cart-vat">{formatSAR(cartVat, lang)}</span>
            </div>
            <div className="flex justify-between text-base font-bold pt-2 border-t border-border">
              <span>{t.total}</span>
              <span data-testid="cart-total">{formatSAR(cartTotal, lang)}</span>
            </div>
            <Button
              className="w-full mt-3"
              size="lg"
              data-testid="cart-checkout-button"
              onClick={() => {
                onOpenChange(false);
                navigate("/cart");
              }}
            >
              {t.checkout}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
